import { Input } from '@frontend-platform/ui-lib/ui';
import { useState } from 'react';
import { useCalibrationEnvironments } from '../../../hooks/use-calibration-environments';
import { formatEnvWithBuild } from '../../../lib/build-format';
import { getCategoryMeta, RUN_NAME_LIMITS } from '../constants';
import {
  dayLabel,
  formatLocalDateShort,
  formatTimeStrShort,
  timeStrToTodayDate,
} from '../scope/utils';
import { type CreateRunFormData, LIVE_START_MODE } from '../types';

interface ConfigureStepProps {
  formData: CreateRunFormData;
  setField: <K extends keyof CreateRunFormData>(key: K, value: CreateRunFormData[K]) => void;
  isNameTaken?: boolean;
}

type EnvHelpers = Pick<
  ReturnType<typeof useCalibrationEnvironments>,
  'getEnvLabel' | 'getEnvOption' | 'isLiveEnv'
>;

// "4 criteria across Quality metrics", "2 criteria across A and B", etc.
function buildCriteriaSummary(formData: CreateRunFormData): string {
  const total = formData.selectedMetrics.reduce((s, g) => s + g.selectedCriteriaCount, 0);
  if (!total) return '—';
  const activeGroups = formData.selectedMetrics
    .filter((g) => g.selectedCriteriaCount > 0)
    .map((g) => getCategoryMeta(g.category).groupName);
  const groupLabel =
    activeGroups.length <= 1
      ? (activeGroups[0] ?? '')
      : `${activeGroups.slice(0, -1).join(', ')} and ${activeGroups[activeGroups.length - 1]}`;
  return `${total} ${total === 1 ? 'criterion' : 'criteria'} across ${groupLabel}`;
}

// Config env + its active build, e.g. "Staging · build_v6 (active)". The
// selected env's current build IS the active build by definition, so the status
// suffix is always "active" here.
function buildEnvironmentSummary(formData: CreateRunFormData, env: EnvHelpers): string {
  const option = env.getEnvOption(formData.evaluationEnvironment);
  return formatEnvWithBuild(
    env.getEnvLabel(formData.evaluationEnvironment),
    option?.manifestBuild,
    'active'
  );
}

function buildScopeSummary(formData: CreateRunFormData, env: EnvHelpers): string {
  const isLive = env.isLiveEnv(formData.evaluationEnvironment);
  if (isLive) {
    const isLater = formData.liveStartMode === LIVE_START_MODE.LATER;
    const now = new Date();
    const startDate = formData.startTime ? timeStrToTodayDate(formData.startTime) : null;
    const endDate = formData.endTime ? timeStrToTodayDate(formData.endTime) : null;
    // For "start later", a wall-clock time already passed today means tomorrow.
    if (isLater && startDate && startDate.getTime() < now.getTime()) {
      startDate.setDate(startDate.getDate() + 1);
    }
    if (endDate && startDate && endDate.getTime() < startDate.getTime()) {
      endDate.setDate(endDate.getDate() + 1);
    }
    const startLabel = !isLater
      ? 'Run Start'
      : startDate
        ? `${formatTimeStrShort(formData.startTime)} ${dayLabel(startDate, now)}`
        : '—';
    const endLabel = endDate
      ? `${formatTimeStrShort(formData.endTime)} ${dayLabel(endDate, now)}`
      : '—';
    return `Live Data | ${startLabel} – ${endLabel} | ${formData.sampleRate}% sample`;
  }
  // Data source is decoupled from the evaluated env, so the scope row names the
  // historical env — which may differ from the one on the Environment row.
  const envText = env.getEnvLabel(formData.historicalEnvironment);
  if (!formData.startTime || !formData.endTime) return envText || '—';
  return `${envText} | ${formatLocalDateShort(formData.startTime)} – ${formatLocalDateShort(formData.endTime)}`;
}

function buildConversationsSummary(formData: CreateRunFormData, env: EnvHelpers): string {
  const count = formData.selectedConversationCount;
  if (count === null || count === undefined) return '—';
  const isLive = env.isLiveEnv(formData.evaluationEnvironment);
  if (isLive) {
    return `~${Number(count).toLocaleString()} conversations (estimated)`;
  }
  return `${Number(count).toLocaleString()} conversations`;
}

export function ConfigureStep({ formData, setField, isNameTaken }: ConfigureStepProps) {
  const { getEnvLabel, getEnvOption, isLiveEnv } = useCalibrationEnvironments();
  const env: EnvHelpers = { getEnvLabel, getEnvOption, isLiveEnv };
  // Defer error display until the user has interacted with the input.
  const [showError, setShowError] = useState(false);
  const trimmed = formData.runName.trim();
  const nameErrorMessage = (() => {
    if (isNameTaken) return 'Run name already exists. Please choose a different name.';
    if (trimmed.length < RUN_NAME_LIMITS.MIN) {
      return `Run name must be at least ${RUN_NAME_LIMITS.MIN} characters.`;
    }
    if (trimmed.length > RUN_NAME_LIMITS.MAX) {
      return `Run name must be at most ${RUN_NAME_LIMITS.MAX} characters.`;
    }
    return null;
  })();

  const rows: Array<{ label: string; value: string }> = [
    { label: 'Criteria', value: buildCriteriaSummary(formData) },
    { label: 'Environment', value: buildEnvironmentSummary(formData, env) },
    { label: 'Scope', value: buildScopeSummary(formData, env) },
    { label: 'Conversations', value: buildConversationsSummary(formData, env) },
  ];

  return (
    // create-run-page.tsx's step content area already owns the single scroll
    // region (`overflow-y-auto`) — a second `overflow-auto h-full` here nested
    // it inside that one, which is what broke wheel-scroll on this step (the
    // other steps don't add their own).
    <div className="flex flex-col gap-6">
      <div className="flex w-1/2 flex-col gap-2">
        <label htmlFor="run-name" className="font-medium text-foreground text-sm">
          Run name
        </label>
        <Input
          id="run-name"
          value={formData.runName}
          maxLength={RUN_NAME_LIMITS.MAX}
          placeholder="ex - production quality check - march"
          onChange={(e) => {
            setShowError(true);
            setField('runName', e.target.value);
          }}
          onBlur={() => setShowError(true)}
        />
        {showError && nameErrorMessage ? (
          <p className="text-red-600 text-xs">{nameErrorMessage}</p>
        ) : null}
      </div>

      {/* Plain semantic table so striping and border-radius cleanly override
          shadcn's row hover/state styles. Mirrors the legacy el-table striped
          layout from aistudio. */}
      <div className="overflow-hidden rounded-lg border border-border">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-border border-b bg-background">
              <th colSpan={2} className="w-52 px-4 py-3 text-left font-semibold text-foreground">
                Run Summary
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr
                key={row.label}
                className={[
                  'border-border',
                  idx < rows.length - 1 ? 'border-b' : '',
                  idx % 2 === 0 ? 'bg-background' : 'bg-muted',
                ].join(' ')}
              >
                <td className="w-52 px-4 py-3 font-semibold text-foreground">{row.label}</td>
                <td className="px-4 py-3 text-foreground">{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
